import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

export type ApproOption = { value: string; label: string };

type Props = {
    value: string | null;
    onChange: (value: string | null) => void;
    options: ApproOption[];
    dataTest?: string;
};

/**
 * Compact approvisionneur picker, styled like the page's cards (plain styled-components — the shadcn combobox
 * clashes with this design). Button → panel with a search input and the delivery person list; clear with the ×.
 */
/** Fixed-position coords for the dropdown panel — anchored to the trigger so it escapes any overflow clip. */
type PanelCoords = { left: number; top?: number; bottom?: number };
const PANEL_MAX_H = 280;

export const ApproPicker = ({ value, onChange, options, dataTest }: Props) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [coords, setCoords] = useState<PanelCoords | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selected = options.find((option) => option.value === value) ?? null;
    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return options;
        return options.filter((option) => option.label.toLowerCase().includes(needle));
    }, [options, query]);

    useEffect(() => {
        if (!open) return;
        setQuery('');
        // Anchor the panel to the trigger in viewport coords; flip above when close to the bottom edge.
        const rect = triggerRef.current?.getBoundingClientRect();
        if (rect) {
            const flipUp = rect.bottom + PANEL_MAX_H > window.innerHeight;
            setCoords({
                left: rect.left,
                ...(flipUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
            });
        }
        inputRef.current?.focus();
        const onOutside = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const onEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        // Fixed panel can't follow an OUTER scroll → close it then. But scrolling INSIDE the panel's own list
        // must NOT close it (that was eating the option list scroll).
        const onScroll = (event: Event) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onOutside);
        document.addEventListener('keydown', onEscape);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('mousedown', onOutside);
            document.removeEventListener('keydown', onEscape);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [open]);

    return (
        <Root ref={rootRef} data-test={dataTest}>
            <Trigger
                ref={triggerRef}
                type="button"
                onClick={() => setOpen((current) => !current)}
                $filled={selected !== null}
            >
                <TriggerLabel>{selected?.label ?? 'Affecter un appro'}</TriggerLabel>
                {selected ? (
                    <ClearButton
                        role="button"
                        aria-label="Retirer l'appro"
                        onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            onChange(null);
                            setOpen(false);
                        }}
                    >
                        <X size={13} />
                    </ClearButton>
                ) : (
                    <ChevronDown size={14} />
                )}
            </Trigger>

            {open && coords && (
                <Panel style={{ left: coords.left, top: coords.top, bottom: coords.bottom }}>
                    <SearchRow>
                        <Search size={13} />
                        <SearchInput
                            ref={inputRef}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Rechercher…"
                        />
                    </SearchRow>
                    <List>
                        {filtered.map((option) => (
                            <Item
                                key={option.value}
                                type="button"
                                $active={option.value === value}
                                onClick={() => {
                                    onChange(option.value);
                                    setOpen(false);
                                }}
                            >
                                <span>{option.label}</span>
                                {option.value === value && <Check size={13} />}
                            </Item>
                        ))}
                        {filtered.length === 0 && <EmptyHint>Aucun appro trouvé</EmptyHint>}
                    </List>
                </Panel>
            )}
        </Root>
    );
};

const Root = styled.div`
    position: relative;
    display: inline-flex;
`;

/* Slim + FIXED width by design: every picker is the same size so dense rows (timeline lanes) stay aligned. */
const Trigger = styled.button<{ $filled: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    width: 160px;
    height: 22px;
    padding: 0 7px;
    font-size: 11px;
    border-radius: 6px;
    border: 1px solid ${({ theme, $filled }) => ($filled ? theme.lightBorderColor : theme.warningColor)};
    background: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme, $filled }) => ($filled ? theme.textColor : theme.warningColor)};
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const TriggerLabel = styled.span`
    flex: 1;
    min-width: 0;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ClearButton = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.infoTextColor};

    &:hover {
        color: ${({ theme }) => theme.errorColor};
    }
`;

/* Fixed → escapes the timeline/section scroll containers; positioned from the trigger rect (inline style). */
const Panel = styled.div`
    position: fixed;
    z-index: 1000;
    width: 230px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 10px;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
    overflow: hidden;
`;

const SearchRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ theme }) => theme.infoTextColor};
`;

const SearchInput = styled.input`
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};

    &::placeholder {
        color: ${({ theme }) => theme.infoTextColor};
    }
`;

const List = styled.div`
    max-height: 240px;
    overflow-y: auto;
    padding: 4px;
`;

const Item = styled.button<{ $active: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 7px 9px;
    border: none;
    border-radius: 7px;
    background: ${({ theme, $active }) =>
        $active ? `color-mix(in srgb, ${theme.ctaPrimaryColor} 12%, transparent)` : 'transparent'};
    color: ${({ theme }) => theme.textColor};
    font-size: 13px;
    text-align: left;
    cursor: pointer;

    span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &:hover {
        background: ${({ theme }) => theme.backgroundColor};
    }
`;

const EmptyHint = styled.div`
    padding: 12px;
    text-align: center;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;
